/*!
 * Copyright © 2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { isElementNull } from '../../../utils';
import { GoogleMapsShapeUtils } from '../../../utils/google-maps-shapes.utils';
import { ICoordinates, IPlaceCategory, IReverseGeocodeRequest, IReverseGeocodeResponse, LatLng } from '../../core/models';
import { delayedRetry } from '../../core/operators';
import { AddressResolutionHttpService, PlacesHttpService } from '../../core/services';
import { ELayoutActions, GetGeofencePlots, OpenGeofencePanel, RefreshGeofencePlot } from '../../core/store/actions';
import { GoogleMapsShape } from '../../maps/models';
import { EGeoShapeType, IGeofenceFormData, ISavePlaceRequest } from '../models';
import {
  EGeofenceActions,
  GetGeofenceAddress,
  GetGeofenceAddressSuccess,
  GetPlaceDropdownCategories,
  GetPlaceDropdownCategoriesSuccess,
  SaveGeofence,
  SaveGeofenceSuccess,
  SetGeofenceCreateOpen,
  SetGeofenceLatLng,
  SetSelectedGeofenceShape
} from './geofence.actions';
import { getGeofenceCurrentShape } from './geofence.selectors';

@Injectable()
export class GeofenceEffects {
  public setSelectedGeofenceShape$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedGeofenceShape>(EGeofenceActions.SetSelectedGeofenceShape),
      map(action => action.payload),
      map((shape: GoogleMapsShape) => (!shape ? null : GoogleMapsShapeUtils.getLatLngFromShape(shape))),
      map(latLng => new SetGeofenceLatLng(latLng))
    )
  );

  public setGeofenceLatLng$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SetGeofenceLatLng>(EGeofenceActions.SetGeofenceLatLng),
      map(action => action.payload),
      map((latLng: LatLng) => new GetGeofenceAddress(latLng))
    )
  );

  public getGeofenceAddress$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetGeofenceAddress>(EGeofenceActions.GetGeofenceAddress),
      map(action => action.payload),
      switchMap((latLng: LatLng) => this._getReverseGeoCodeAddress(latLng)),
      map(geofenceAddress => new GetGeofenceAddressSuccess(geofenceAddress))
    )
  );

  public getPlaceDropdownCategories$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetPlaceDropdownCategories>(EGeofenceActions.GetPlaceDropdownCategories),
      switchMap(() =>
        this._placesHttpService.getPlaceCategoriesDropdown().pipe(
          delayedRetry(),
          catchError(() => of([] as IPlaceCategory[]))
        )
      ),
      map(categories => new GetPlaceDropdownCategoriesSuccess(categories))
    )
  );

  public saveGeofence$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SaveGeofence>(EGeofenceActions.SaveGeofence),
      withLatestFrom(this._store.pipe(select(getGeofenceCurrentShape))),
      map(([action, currentGeofenceShape]) => ({
        request: this._mapToSavePlaceRequest(action.payload, currentGeofenceShape),
        geofenceId: action.payload.id
      })),
      switchMap(({ request, geofenceId }) =>
        this._placesHttpService.saveGeofence(request).pipe(
          delayedRetry(),
          map(() => new SaveGeofenceSuccess(geofenceId)),
          catchError(() => of(new SaveGeofenceSuccess()))
        )
      )
    )
  );

  public refreshGeofencePlotsOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SaveGeofenceSuccess>(EGeofenceActions.SaveGeofenceSuccess),
      map(action => (action.payload ? new RefreshGeofencePlot(action.payload) : new GetGeofencePlots()))
    )
  );

  public onOpenGeofencePanel$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenGeofencePanel>(ELayoutActions.OpenGeofencePanel),
      map(() => new SetGeofenceCreateOpen(true))
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store,
    private readonly _addressResolutionHttpService: AddressResolutionHttpService,
    private readonly _placesHttpService: PlacesHttpService
  ) {}

  private _getReverseGeoCodeAddress(latLng: LatLng): Observable<IReverseGeocodeResponse> {
    if (!latLng) {
      return of(null);
    }

    const reverseGeocodeRequest: IReverseGeocodeRequest = {
      key: 'placeaddress',
      data: {
        Latitude: latLng.lat,
        Longitude: latLng.lng
      }
    };

    return this._addressResolutionHttpService.getReverseGeocode([reverseGeocodeRequest]).pipe(
      delayedRetry(),
      catchError(() => of(null)),
      filter(response => !isElementNull(response)),
      map(result => result[0])
    );
  }

  private _mapToSavePlaceRequest(geofenceFormData: IGeofenceFormData, geofenceMapShape: GoogleMapsShape): ISavePlaceRequest {
    const shapeBounds = GoogleMapsShapeUtils.getBoundsFromShape(geofenceMapShape);
    const shapeType = this._getShapeTypeFromGoogleMapsShape(geofenceMapShape);
    const shapecoords = this._getShapeCoords(geofenceMapShape);
    const placeRequest: ISavePlaceRequest = {
      Place: {
        address: {
          FormattedAddress: geofenceFormData.address.addr.formattedAddress,
          AddressLine1: geofenceFormData.address.addr.addressLine1,
          AddressLine2: geofenceFormData.address.addr.addressLine2,
          City: geofenceFormData.address.addr.city,
          State: geofenceFormData.address.addr.state,
          PostalCode: geofenceFormData.address.addr.postalCode,
          Country: geofenceFormData.address.addr.country,
          Latitude: geofenceFormData.address.addr.latitude,
          Longitude: geofenceFormData.address.addr.longitude,
          AddressComponents: geofenceFormData.address.addrComponents
        },
        addressComponents: geofenceFormData.address.addrComponents,
        fulladdress: geofenceFormData.address.fulladdress,
        id: geofenceFormData.id, // -1 is new geofence
        name: geofenceFormData.name,
        desc: geofenceFormData.description,
        catname: geofenceFormData.category.Description,
        catid: geofenceFormData.category.Id,
        groupids: geofenceFormData.groups.map(g => g.id).join(','),
        cname: geofenceFormData.contactName,
        cno: geofenceFormData.contactPhoneNumber,
        clientid: geofenceFormData.clientID,
        smap: geofenceFormData.showOnMap,
        saddr: geofenceFormData.showOnAddress,
        shapetype: shapeType,
        shapecoords: null,
        radius: GoogleMapsShapeUtils.getRadiusFromBounds(shapeBounds),
        center: {
          Latitude: shapeBounds.getCenter().lat(),
          Longitude: shapeBounds.getCenter().lng()
        },
        swbound: {
          Latitude: shapeBounds.getSouthWest().lat(),
          Longitude: shapeBounds.getSouthWest().lng()
        },
        nebound: {
          Latitude: shapeBounds.getNorthEast().lat(),
          Longitude: shapeBounds.getNorthEast().lng()
        },
        type: geofenceFormData.placeType,
        caticon: geofenceFormData.category.ImageCssClass,
        catcolor: geofenceFormData.category.Color,
        editlabel: ''
      },
      ShapeCoOrdinates: shapecoords
    };
    return placeRequest;
  }

  private _getShapeTypeFromGoogleMapsShape(googleShape: GoogleMapsShape): EGeoShapeType {
    return googleShape instanceof google.maps.Polygon
      ? EGeoShapeType.GeoPolygon
      : googleShape instanceof google.maps.Rectangle
      ? EGeoShapeType.GeoRectangle
      : EGeoShapeType.GeoCircle;
  }

  private _getShapeCoords(geofenceMapShape: GoogleMapsShape) {
    const pathArray: ICoordinates[] = [];

    if (geofenceMapShape instanceof google.maps.Polygon) {
      geofenceMapShape.getPaths().forEach(path => {
        path.forEach(latLng => {
          pathArray.push({ Latitude: latLng.lat(), Longitude: latLng.lng() });
        });
      });
    }

    if (geofenceMapShape instanceof google.maps.Rectangle) {
      const bounds = GoogleMapsShapeUtils.getBoundsFromShape(geofenceMapShape);
      pathArray.push({ Latitude: bounds.getNorthEast().lat(), Longitude: bounds.getNorthEast().lng() });
      pathArray.push({ Latitude: bounds.getNorthEast().lat(), Longitude: bounds.getSouthWest().lng() });
      pathArray.push({ Latitude: bounds.getSouthWest().lat(), Longitude: bounds.getSouthWest().lng() });
      pathArray.push({ Latitude: bounds.getSouthWest().lat(), Longitude: bounds.getNorthEast().lng() });
    }

    return pathArray;
  }
}
